import React, { useState } from "react";
import BestPracticesGapsChart from "./BestPracticesGapsChart";

function parseBulletPoints(text) {
  if (!text || typeof text !== "string") return [];

  return text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.startsWith("•"))
    .map((line) => line.substring(1).trim());
}

function parseSourceLinks(text) {
  if (!text || typeof text !== "string") return [];

  return text
    .split(/\d+\./)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

export default function ScreeningPolicyCard({ policyData }) {
  const [activeTab, setActiveTab] = useState("overview");

  if (!policyData) {
    return (
      <div className="chart-card policy-card">
        <p style={{ padding: 20, textAlign: "center", opacity: 0.6 }}>
          No screening policy data available
        </p>
      </div>
    );
  }

  const {
    Country,
    Summary,
    Screening,
    "Cancer Registry": CancerRegistry,
    "Best Practices": BestPractices,
    Gaps,
    "Genetic Counseling": GeneticCounseling,
    "Health Promotion": HealthPromotion,
    Sources,
  } = policyData;

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "screening", label: "Screening Details" },
    { id: "registry", label: "Registry & Genetics" },
    { id: "practices", label: "Best Practices vs Gaps" },
    { id: "sources", label: "Sources" },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <div className="policy-content">
            <h4 className="policy-section-title">Summary</h4>
            <p className="policy-text">{Summary || "No summary available."}</p>
          </div>
        );

      case "screening":
        return (
          <div className="policy-content">
            <h4 className="policy-section-title">Screening Programme</h4>
            <p className="policy-text">
              {Screening || "No screening information available."}
            </p>
          </div>
        );

      case "registry":
        return (
          <div className="policy-content">
            <h4 className="policy-section-title">Cancer Registry</h4>
            <p className="policy-text">
              {CancerRegistry || "No cancer registry information available."}
            </p>

            {GeneticCounseling && (
              <>
                <h4
                  className="policy-section-title"
                  style={{ marginTop: "20px" }}
                >
                  Genetic Counseling
                </h4>
                <p className="policy-text">{GeneticCounseling}</p>
              </>
            )}

            {HealthPromotion && (
              <>
                <h4
                  className="policy-section-title"
                  style={{ marginTop: "20px" }}
                >
                  Health Promotion
                </h4>
                <p className="policy-text">{HealthPromotion}</p>
              </>
            )}
          </div>
        );

      case "practices":
        return (
          <div className="policy-content">
            <div style={{ marginBottom: "24px" }}>
              <h4 className="policy-section-title best-practices-title">
                <span className="icon-checkmark">✓</span> Best Practices
              </h4>
              {BestPractices ? (
                <ul className="best-practices-list">
                  {parseBulletPoints(BestPractices).map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p className="policy-text" style={{ opacity: 0.6 }}>
                  No best practices listed.
                </p>
              )}
            </div>

            <div style={{ marginBottom: "24px" }}>
              <h4 className="policy-section-title gaps-title">
                <span className="icon-warning">⚠</span> Gaps
              </h4>
              {Gaps ? (
                <ul className="gaps-list">
                  {parseBulletPoints(Gaps).map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p className="policy-text" style={{ opacity: 0.6 }}>
                  No gaps listed.
                </p>
              )}
            </div>

            <div
              style={{
                height: "300px",
                marginTop: "32px",
                border: "1px solid var(--border-color)",
                borderRadius: "8px",
                padding: "12px",
              }}
            >
              <BestPracticesGapsChart
                bestPractices={BestPractices}
                gaps={Gaps}
              />
            </div>
          </div>
        );

      case "sources":
        return (
          <div className="policy-content">
            <h4 className="policy-section-title">References & Sources</h4>
            {Sources ? (
              <ol className="sources-list">
                {parseSourceLinks(Sources).map((source, idx) => (
                  <li key={idx} className="source-item">
                    {source}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="policy-text" style={{ opacity: 0.6 }}>
                No sources available.
              </p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="chart-card policy-card">
      <div className="policy-header">
        <h3 className="policy-title">
          CRC Screening Policy Profile: {Country}
        </h3>
      </div>

      <div className="policy-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`policy-tab ${activeTab === tab.id ? "active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="policy-body">{renderTabContent()}</div>
    </div>
  );
}
